import { cloneElement, useEffect, useState } from 'react'
import { cls } from '@/ui/cls'
import { Ico } from '@/ui/Ico'
import { Eyebrow } from '@/ui'
import HandoverCard from './HandoverCard'
import TimelineTab from './TimelineTab'
import DetailsTab from './DetailsTab'
import CompanyIntel from './CompanyIntel'
import ReferralSheet from '@/features/leads/focus/ReferralSheet'
import OutcomeBar from '@/features/leads/workflow/OutcomeBar'
import { DoNotContactNotice, DoNotContactSheet, useCanMarkDoNotContact } from '@/features/leads/workflow/DoNotContact'
import { blocksOutcomeShortcut, leadActions } from '@/features/leads/workflow/doNotContactRules'
import { trackEvent } from '@/shared/api/telemetry'
import type { KeyTrigger } from '@/shared/api/trackMeta'
import { useLeadDetail } from './useLeadDetail'
import { BrandLoader } from '@/shared/white-label/BrandLoader'
import type { LeadRow } from '@/shared/types'
import type { OutcomeKind } from '@/shared/domain'
import * as m from 'motion/react-m'

type OverlayTab = 'handover' | 'timeline' | 'details' | 'company'

interface Props {
  lead: LeadRow
  onClose: () => void
  onAction: (kind: OutcomeKind, comment?: string, via?: KeyTrigger) => void
  notesOpen: boolean
  onSetNotesOpen: (open: boolean) => void
  /** True while the outcome sheet is open for this lead — the overlay's own
   *  shortcut keys stand down so App's global handler (rating keys, Esc,
   *  ⌘Enter) is the only thing listening. */
  outcomeOpen?: boolean
  /** Absent hides Postpone from the bar — callers that can't postpone (none
   *  today) simply don't pass it. */
  onPostpone?: (leadId: string, days: number) => void
  /** False when this replaces LeadOverlayLoading, whose panel is already in. */
  slideIn?: boolean
}

function TabButton({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cls(
        'bg-transparent border-0 py-3 px-1 mx-3 first:ml-0 text-xs font-medium cursor-pointer',
        'inline-flex items-center gap-1.5 tracking-[0.01em] -mb-px',
        'border-b border-transparent',
        active ? 'text-ink-1000 border-b-ink-1000' : 'text-ink-500 hover:text-ink-1000',
      )}
    >
      {children}
    </button>
  )
}

/**
 * The backdrop, the panel and its header: the part of the overview that does
 * not depend on the lead, so the loading state and the lead share it.
 */
function OverlayFrame({ onClose, notesOpen, onSetNotesOpen, slideIn = true, children }: {
  onClose: () => void
  notesOpen: boolean
  onSetNotesOpen: (open: boolean) => void
  slideIn?: boolean
  children: React.ReactNode
}) {
  const closeIcon = cloneElement(Ico.x, { width: 12, height: 12 })
  return (
    // z-50 is the layer every Radix dialog, sheet, popover, select and tooltip
    // uses. They portal to the end of <body>, after the app root, so at the
    // same z they paint above this overlay. A higher z hid the contact editor
    // behind it (YCRM-100); layering.test.ts holds the rule.
    <m.div
      className="fixed inset-0 z-50 bg-ink-1000/35 flex justify-end"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      initial={slideIn ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
    >
      <m.aside
        className={cls(
          'h-screen bg-paper border-l border-ink-200 flex flex-col overflow-hidden',
          notesOpen ? 'w-[min(1040px,96vw)]' : 'w-[min(720px,92vw)]',
        )}
        onClick={e => e.stopPropagation()}
        initial={slideIn ? { x: '100%' } : false}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ duration: 0.28, ease: [0.2, 0.7, 0.2, 1] }}
      >
        <header className="flex items-center justify-between px-[22px] py-3.5 bg-paper border-b border-ink-200 gap-3">
          <Eyebrow as="span" size="sm" gap="none">Leadöversikt</Eyebrow>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onSetNotesOpen(!notesOpen)}
              title={notesOpen ? 'Dölj aktivitetspanelen' : 'Visa aktivitetspanelen'}
              aria-pressed={notesOpen}
              className={cls(
                'inline-flex items-center gap-1.5 h-[30px] px-2.5 border cursor-pointer',
                'text-xs font-medium rounded-[2px] transition-colors [&_svg]:w-3.5 [&_svg]:h-3.5',
                notesOpen
                  ? 'bg-brand border-brand text-ink-1000 hover:bg-brand-deep'
                  : 'bg-transparent border-ink-200 text-ink-700 hover:text-ink-1000 hover:border-ink-1000',
              )}
            >
              {Ico.edit}
              <span>Aktivitet</span>
            </button>
            <button
              className="bg-transparent border border-ink-200 w-[30px] h-[30px] rounded grid place-items-center leading-none text-ink-500 hover:text-ink-1000 hover:border-ink-1000 cursor-pointer p-0"
              onClick={onClose}
              aria-label="Stäng lead"
              title="Stäng lead"
            >
              {closeIcon}
            </button>
          </div>
        </header>
        {children}
      </m.aside>
    </m.div>
  )
}

/**
 * The overview opened by a link to a lead this browser has not loaded yet:
 * the panel slides in at once and the mark holds it until the lead arrives.
 */
export function LeadOverlayLoading({ onClose, notesOpen, onSetNotesOpen }: {
  onClose: () => void
  notesOpen: boolean
  onSetNotesOpen: (open: boolean) => void
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <OverlayFrame onClose={onClose} notesOpen={notesOpen} onSetNotesOpen={onSetNotesOpen}>
      <BrandLoader label="Laddar lead" className="flex-1 min-h-0" />
    </OverlayFrame>
  )
}

export default function LeadOverlay({
  lead: leadSummary, onClose, onAction,
  notesOpen, onSetNotesOpen,
  outcomeOpen = false, onPostpone, slideIn = true,
}: Props) {
  // The table hands us a list row; overlay the full card fields.
  const lead = useLeadDetail(leadSummary)
  const [tab, setTab] = useState<OverlayTab>('handover')
  // Referral capture — same flow as Focus: the sheet writes the referred
  // person onto the lead and promotes them, then hands the note to the
  // outcome flow, which logs the call and asks when to ring the referral.
  const [referring, setReferring] = useState(false)
  const [markingDnc, setMarkingDnc] = useState(false)
  const canMarkDnc = useCanMarkDoNotContact()
  const actions = leadActions(lead)

  const onTab = (t: OverlayTab) => {
    trackEvent('lead_detail_tab', { tab: t }, lead.id)
    setTab(t)
  }

  // Same keyboard letters as Focus (R N B X C W E, P for Postpone), so
  // "press B" means the same thing whether the lead is open full-screen or
  // in this overlay. Stands down entirely while the outcome sheet is open —
  // that is App's global handler's job (rating keys, Esc, Cmd/Ctrl+Enter).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // App's global handler already closed the outcome sheet for this
        // same keystroke (and called preventDefault to say so) — don't also
        // close the overlay underneath it.
        if (!e.defaultPrevented) onClose()
        return
      }
      if (outcomeOpen) return
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const k = e.key.toLowerCase()
      const handled = ['r', 'n', 'b', 'x', 'c', 'w', 'e', 'p'].includes(k)
      if (!handled || blocksOutcomeShortcut(lead, k)) return
      e.preventDefault()
      const via: KeyTrigger = { source: 'keyboard', key: k.toUpperCase() }
      if (k === 'r') onAction('reached', undefined, via)
      else if (k === 'n') onAction('voicemail', undefined, via)
      else if (k === 'b') onAction('booked', undefined, via)
      else if (k === 'x') onAction('notfit', undefined, via)
      else if (k === 'c') onAction('customer', undefined, via)
      else if (k === 'w') onAction('bad_contact', undefined, via)
      else if (k === 'e') onAction('email_sent', undefined, via)
      else if (k === 'p') onPostpone?.(lead.id, 7)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose, outcomeOpen, onAction, onPostpone, lead])

  return (
    <>
    <OverlayFrame onClose={onClose} notesOpen={notesOpen} onSetNotesOpen={onSetNotesOpen} slideIn={slideIn}>
      <div className={cls(
        'flex-1 min-h-0 grid',
        notesOpen ? 'grid-cols-[1fr_320px]' : 'grid-cols-1',
      )}>
        <div className="flex flex-col min-h-0 min-w-0 overflow-hidden">
          <nav className="flex gap-1 px-[22px] bg-paper border-b border-ink-200">
            <TabButton active={tab === 'handover'} onClick={() => onTab('handover')}>Överlämning</TabButton>
            <TabButton active={tab === 'timeline'} onClick={() => onTab('timeline')}>
              Aktivitet <span className="font-mono text-[10px] text-ink-500">
                {lead.timeline.length + lead.notes.length}
              </span>
            </TabButton>
            <TabButton active={tab === 'details'} onClick={() => onTab('details')}>Detaljer</TabButton>
            <TabButton active={tab === 'company'} onClick={() => onTab('company')}>Företag</TabButton>
          </nav>

          <div className="flex-1 min-h-0 overflow-auto p-0 [&>.handover]:border-0">
            {tab === 'handover' && (
              <HandoverCard
                lead={lead}
                mode="drawer"
              />
            )}
            {tab === 'timeline' && <TimelineTab lead={lead} onAction={onAction} />}
            {tab === 'details' && <DetailsTab lead={lead} />}
            {tab === 'company' && <CompanyIntel lead={lead} />}
          </div>
        </div>
        {notesOpen && (
          <TimelineTab
            lead={lead}
            onAction={onAction}
            panel
            onClose={() => onSetNotesOpen(false)}
          />
        )}
      </div>

      <footer className="px-[22px] py-3 pt-3 pb-4 bg-paper border-t border-ink-200">
        {actions.kind === 'doNotContact' ? (
          <DoNotContactNotice lead={lead} mark={actions.mark} />
        ) : (
          <OutcomeBar
            onAction={onAction}
            onReferStart={() => setReferring(true)}
            onPostpone={onPostpone ? days => onPostpone(lead.id, days) : undefined}
            onDoNotContact={canMarkDnc ? () => setMarkingDnc(true) : undefined}
          />
        )}
      </footer>
    </OverlayFrame>

    {/* Sibling of the backdrop, not a child — Radix portals its content out of
        this subtree in the DOM, but React still bubbles clicks up through the
        component tree, so nesting this inside the backdrop's onClick={onClose}
        would close the overlay on every click inside the sheet. */}
    {referring && (
      <ReferralSheet
        key={lead.id}
        lead={lead}
        onCancel={() => setReferring(false)}
        onDone={note => { setReferring(false); onAction('referred', note) }}
      />
    )}
    {markingDnc && (
      <DoNotContactSheet
        key={lead.id}
        lead={lead}
        mode="mark"
        onCancel={() => setMarkingDnc(false)}
        onDone={() => setMarkingDnc(false)}
      />
    )}
    </>
  )
}
